import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraHeading, SpecraInlineBanner, SpecraText } from '@mono/ui-components';
import { useState } from 'react';
import { artifactClient, projectDataClient } from '../../../../lib/connect/clients';
// These values come from an immutable capture, never from today's extraction.
function parsed(json) {
    try {
        return JSON.parse(json);
    } catch  {
        return null;
    }
}
export function PreservedContent({ json }) {
    const value = parsed(json);
    return value ? /*#__PURE__*/ _jsx(Content, {
        value: value
    }) : /*#__PURE__*/ _jsx(SpecraText, {
        children: "Saved context is absent or unreadable. Open the exact source to review it."
    });
}
function Content({ value }) {
    if (typeof value === 'string') {
        const nested = parsed(value);
        return nested && typeof nested === 'object' ? /*#__PURE__*/ _jsx(Content, {
            value: nested
        }) : /*#__PURE__*/ _jsx("span", {
            className: "whitespace-pre-wrap break-words",
            children: value
        });
    }
    if (Array.isArray(value)) return /*#__PURE__*/ _jsx("div", {
        className: "space-y-2",
        children: value.map((v, i)=>/*#__PURE__*/ _jsx(Content, {
                value: v
            }, i))
    });
    if (value && typeof value === 'object') return /*#__PURE__*/ _jsx("dl", {
        className: "space-y-1",
        children: Object.entries(value).filter(([k, v])=>v !== null && v !== '' && !/^(tenant_?id|project_?id|id|draft_?id|attachment_?id|file_?path|request_?hash|deleted_?at)$/i.test(k)).map(([k, v])=>/*#__PURE__*/ _jsxs("div", {
                className: "pl-2 border-l border-border",
                children: [
                    /*#__PURE__*/ _jsx("dt", {
                        className: "text-muted-foreground text-xs",
                        children: k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ')
                    }),
                    /*#__PURE__*/ _jsx("dd", {
                        children: /*#__PURE__*/ _jsx(Content, {
                            value: v
                        })
                    })
                ]
            }, k))
    });
    return /*#__PURE__*/ _jsx("span", {
        children: value === null ? 'Not recorded' : String(value)
    });
}
export function SourceVersion({ projectId, version }) {
    const [busy, setBusy] = useState(false);
    const [failure, setFailure] = useState('');
    const [preview, setPreview] = useState('');
    const snapshot = parsed(version.sourceSnapshotJson);
    const hasContext = !!(snapshot?.Requirements?.length || snapshot?.Facts?.length);
    async function openSource() {
        if (busy) return;
        setBusy(true);
        setFailure('');
        setPreview('');
        try {
            // Reauthorize and compare the attachment hash before obtaining any URL.
            const fresh = await projectDataClient.readSolicitationReview({
                projectId
            });
            const saved = fresh.state?.versions.find((v)=>v.id === version.id);
            if (!saved || saved.attachmentId !== version.attachmentId || saved.sourceHash !== version.sourceHash) throw new Error('Source identity unavailable');
            const { artifact } = await artifactClient.getArtifact({
                artifactId: version.attachmentId
            });
            if (!artifact || artifact.projectId !== projectId || artifact.deletedAt) throw new Error('Source unavailable');
            if (version.sourceHash && artifact.checksumSha256 !== version.sourceHash) throw new Error('Source changed');
            const { url } = await artifactClient.generateDownloadUrl({
                artifactId: version.attachmentId,
                preferProcessed: false
            });
            if (!url) throw new Error('Download unavailable');
            if (artifact.mimeType.startsWith('text/')) {
                const response = await fetch(url);
                if (!response.ok) throw new Error('Source unavailable');
                const bytes = await response.arrayBuffer();
                if (version.sourceHash) {
                    const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), (b)=>b.toString(16).padStart(2, '0')).join('');
                    if (hash !== version.sourceHash) throw new Error('Source changed');
                }
                setPreview(new TextDecoder().decode(bytes));
            } else {
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.download = version.sourceName;
                link.click();
            }
        } catch  {
            setFailure('Exact source unavailable, changed, or access denied. Saved excerpts below remain the issued context; current bytes have not been presented as the old source. Retry opening the source, or upload a new attachment if it changed.');
        } finally{
            setBusy(false);
        }
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 py-2",
        children: [
            /*#__PURE__*/ _jsxs(SpecraHeading, {
                size: "sm",
                children: [
                    "Version ",
                    version.versionNumber,
                    " · ",
                    version.sourceName
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "caption",
                children: [
                    "Issued ",
                    new Date(version.issuedAt).toLocaleString(),
                    " · by ",
                    version.issuedBy
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "caption",
                children: [
                    "Source ",
                    version.attachmentId,
                    " · SHA-256",
                    ' ',
                    version.sourceHash || 'not recorded — original bytes cannot be verified'
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                disabled: busy,
                onClick: ()=>void openSource(),
                children: busy ? 'Opening exact source…' : 'Open / download exact source'
            }),
            !version.sourceHash && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "caption",
                children: "This action opens the current attachment. Its identity is preserved, but its bytes cannot be certified as the original snapshot."
            }),
            failure && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: failure
            }),
            preview && /*#__PURE__*/ _jsx("blockquote", {
                className: "whitespace-pre-wrap border-l border-border pl-3",
                children: preview
            }),
            /*#__PURE__*/ _jsx(SpecraHeading, {
                size: "sm",
                children: "Saved requirement and fact excerpts"
            }),
            hasContext ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Content, {
                        value: snapshot?.Requirements
                    }),
                    /*#__PURE__*/ _jsx(Content, {
                        value: snapshot?.Facts
                    })
                ]
            }) : /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                children: "No extracted context was saved at issuance. Extraction is pending or absent. Open the exact source above; no automated diff is available."
            })
        ]
    });
}
